/**
 * ================================================================================================
 *  THE RECORD'S IDENTITY, FIXED ABOVE EVERYTHING ELSE. **2026-10-25.**
 * ================================================================================================
 *
 * Owner: *"בתוך כרטיס החברה כותרת קבועה עם שם ומספר רשמי, טאבים לפי נושא וקבוצות שדות מתקפלות"*.
 *
 * The thing a person must never lose while they edit a record is WHICH RECORD THEY ARE EDITING.
 * The company card used to put the name in the shell title and the official number four blocks
 * down inside a fact list, so by the time you reached the lifecycle form the only confirmation of
 * which client you were about to mark as former was the browser tab.
 *
 * So: the name, the official number and the state, together, above the tabs, always.
 *
 * ── THE OFFICIAL NUMBER IS `dir="ltr"` AND THE NAME IS NOT ──────────────────────────────────────
 *
 * A nine-digit number inside a right-to-left paragraph renders in an order nobody can read back to
 * a tax authority. It is marked as the Latin-direction run it is, which is the same treatment it
 * gets in every list in this product.
 *
 * ── AND IT SAYS WHEN THERE IS NO NUMBER ─────────────────────────────────────────────────────────
 *
 * `null` prints a named absence and not an empty space. **A company with no official number is a
 * real state** — `core.company.legal_entity_id` is nullable — and an empty slot in a header reads
 * as "the screen failed to load it", which is a different fact entirely.
 */

import type { ReactNode } from 'react';

export interface RecordHeaderProps {
  /** The name a person recognises. Usually the display name, falling back to the legal one. */
  readonly titleHe: string;
  /** The legal name, when it differs from the title. Omitted ⇒ not shown; they are often equal. */
  readonly legalNameHe?: string | null;
  /** The official number. `null` ⇒ the named absence below, never a blank. */
  readonly officialNumber: string | null;
  /** What the record's official number is called here — ח.פ, ע.מ, מספר ארגון. */
  readonly officialNumberLabelHe: string;
  /** A chip or two: lifecycle, demo, anything the SERVER stated about this record. */
  readonly marks?: ReactNode;
  /** The one or two actions that belong to the record as a whole, never to one tab. */
  readonly actions?: ReactNode;
}

export function RecordHeader({
  titleHe,
  legalNameHe,
  officialNumber,
  officialNumberLabelHe,
  marks,
  actions,
}: RecordHeaderProps): JSX.Element {
  const differentLegalName =
    typeof legalNameHe === 'string' && legalNameHe.trim() !== '' && legalNameHe !== titleHe;

  return (
    <header className="record-head">
      <div className="record-head__identity">
        <h2 className="record-head__name">{titleHe}</h2>
        <p className="record-head__number">
          <span className="record-head__number-label">{officialNumberLabelHe}</span>{' '}
          {typeof officialNumber === 'string' && officialNumber.trim() !== '' ? (
            <span dir="ltr">{officialNumber}</span>
          ) : (
            // Named, not blank. See the header of this file.
            <span className="record-head__number-absent">טרם נרשם</span>
          )}
        </p>
        {differentLegalName ? (
          <p className="record-head__legal">שם רשמי: {legalNameHe}</p>
        ) : null}
        {marks === undefined ? null : <div className="record-head__marks">{marks}</div>}
      </div>
      {actions === undefined ? null : <div className="record-head__actions">{actions}</div>}
    </header>
  );
}
